import { useState } from "react";
import { toast } from "sonner";
import { PageShell } from "@/components/site/PageShell";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { JobTimeline } from "@/components/shared/JobTimeline";
import { JobDetails } from "@/components/shared/JobDetails";
import { api, errMsg } from "@/lib/api";
import { BRAND_PHOTOS } from "@/lib/constants";

export default function Track() {
  const [stage, setStage] = useState("lookup");
  const [ref, setRef] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [job, setJob] = useState(null);
  const [token, setTokenState] = useState(null);
  const [busy, setBusy] = useState(false);

  const send = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/jobs/track/send-otp", { reference: ref, email });
      toast.success(r.data.message);
      setStage("otp");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const r = await api.post("/jobs/track/verify-otp", { reference: ref, email, code });
      setJob(r.data.job);
      setTokenState(r.data.token);
      setStage("job");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell testId="track-page" overline="Track my job" title="Where's my car?" image={BRAND_PHOTOS[4]}
      intro="Enter your job reference and the email we have on file. We'll send a 6-digit code to confirm it's you.">
      {stage === "lookup" && (
        <form onSubmit={send} className="max-w-md rounded-2xl bg-white p-6 space-y-4" data-testid="track-lookup-form">
          <Input required placeholder="Job reference (e.g. JOB-2026-0001)" value={ref} onChange={(e) => setRef(e.target.value.toUpperCase())} data-testid="track-job-reference-input" />
          <Input required type="email" placeholder="Email on file" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="track-job-email-input" />
          <button disabled={busy} data-testid="track-job-send-otp-button" className="w-full rounded-full bg-[#020165] text-white font-semibold py-3 hover:bg-[#0a0a8a] disabled:opacity-60 transition-colors">{busy ? "Sending…" : "Send me a code"}</button>
        </form>
      )}
      {stage === "otp" && (
        <div className="max-w-md rounded-2xl bg-white p-6 space-y-5" data-testid="track-otp-form">
          <p className="text-sm text-[#56585E]">If <strong>{ref}</strong> matches <strong>{email}</strong>, a code is on its way. It expires in 10 minutes.</p>
          <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="track-job-otp-input">
            <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} data-testid={`track-job-otp-slot-${i}`} />)}</InputOTPGroup>
          </InputOTP>
          <button disabled={busy || code.length !== 6} onClick={verify} data-testid="track-job-verify-otp-button" className="w-full rounded-full bg-[#F86900] text-[#0D141A] font-bold py-3 hover:bg-[#ff7d1f] disabled:opacity-50 transition-colors">{busy ? "Checking…" : "View my job"}</button>
          <div className="flex justify-between text-sm"><button onClick={() => setStage("lookup")} className="underline" data-testid="track-change-details">Change details</button><button onClick={send} className="underline" data-testid="track-resend-otp">Resend code</button></div>
        </div>
      )}
      {stage === "job" && job && (
        <div className="space-y-6" data-testid="track-job-result">
          <div className="rounded-2xl bg-white p-6"><JobTimeline job={job} /></div>
          <JobDetails job={job} token={token} />
        </div>
      )}
    </PageShell>
  );
}
